
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Upload, Image, Plus, Trash2, Video, X, Link as LinkIcon } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import SimpleRichTextEditor from "@/components/blog/SimpleRichTextEditor";

interface Lesson {
  id: string;
  title: string;
  description: string;
  videoUrl: string;
}

const CreateCourse = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [level, setLevel] = useState("");
  const [duration, setDuration] = useState("");
  const [price, setPrice] = useState("");
  const [status, setStatus] = useState("Coming Soon");
  const [published, setPublished] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [whatYouLearn, setWhatYouLearn] = useState<string[]>(['']);
  const [textDirection, setTextDirection] = useState<"ltr" | "rtl">("ltr");
  
  const courseImageInputRef = useRef<HTMLInputElement>(null);
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleCourseImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      
      console.log("Course image selected:", file.name, file.type, file.size);
    }
  };

  const addLesson = () => {
    const newLesson: Lesson = {
      id: Date.now().toString(),
      title: "",
      description: "",
      videoUrl: ""
    };
    setLessons([...lessons, newLesson]);
  };

  const removeLesson = (id: string) => {
    setLessons(lessons.filter(lesson => lesson.id !== id));
  };

  const updateLesson = (id: string, field: keyof Lesson, value: any) => {
    setLessons(lessons.map(lesson => 
      lesson.id === id ? { ...lesson, [field]: value } : lesson
    ));
  };

  const createStorageBucket = async (bucketName: string) => {
    try {
      console.log(`Using bucket: ${bucketName}`);
      return true;
    } catch (error: any) {
      console.error(`Error with bucket ${bucketName}:`, error.message);
      return false;
    }
  };

  const uploadCourseImage = async (): Promise<string | null> => {
    if (!imageFile || !user) return null;
    
    try {
      await createStorageBucket('course-images');
      
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;
      
      console.log("Uploading course image:", filePath);
      
      const { data, error } = await supabase.storage
        .from('course-images')
        .upload(filePath, imageFile, {
          cacheControl: '3600',
          upsert: true
        });
      
      if (error) throw error;
      
      console.log("Course image uploaded successfully:", data);
      
      const { data: urlData } = supabase.storage
        .from('course-images')
        .getPublicUrl(filePath);
        
      console.log("Course image public URL:", urlData.publicUrl);
      return urlData.publicUrl;
    } catch (error: any) {
      console.error('Error uploading image:', error.message);
      toast({
        title: "Image upload failed",
        description: error.message,
        variant: "destructive"
      });
      return null;
    }
  };

  const addLearningItem = () => {
    setWhatYouLearn([...whatYouLearn, '']);
  };

  const removeLearningItem = (index: number) => {
    const updatedItems = [...whatYouLearn];
    updatedItems.splice(index, 1);
    setWhatYouLearn(updatedItems);
  };

  const updateLearningItem = (index: number, value: string) => {
    const updatedItems = [...whatYouLearn];
    updatedItems[index] = value;
    setWhatYouLearn(updatedItems);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title || !description || !level || !duration || !price) {
      toast({
        title: "Missing information",
        description: "Please fill in all required course fields.",
        variant: "destructive",
      });
      return;
    }

    for (const lesson of lessons) {
      if (!lesson.title || !lesson.description) {
        toast({
          title: "Incomplete lesson",
          description: "Please fill in all required fields for each lesson.",
          variant: "destructive",
        });
        return;
      }
    }

    if (!user) {
      toast({
        title: "Authentication required",
        description: "You must be logged in to create a course.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      let imageUrl = "/placeholder.svg";
      if (imageFile) {
        const uploadedUrl = await uploadCourseImage();
        if (uploadedUrl) imageUrl = uploadedUrl;
      }
      
      const filteredWhatYouLearn = whatYouLearn.filter(item => item.trim() !== '');
      
      const { data: courseData, error: courseError } = await supabase.from('courses').insert({
        user_id: user.id,
        title,
        description,
        level,
        duration,
        price,
        status,
        published,
        image_url: imageUrl,
        what_you_learn: filteredWhatYouLearn.length > 0 ? filteredWhatYouLearn : null,
        text_direction: textDirection,
      }).select();
      
      if (courseError) throw courseError;
      
      const courseId = courseData[0].id;
      
      if (lessons.length > 0) {
        for (const lesson of lessons) {
          const { error: lessonError } = await supabase
            .from('course_lessons')
            .insert({
              course_id: courseId,
              title: lesson.title,
              description: lesson.description,
              video_url: lesson.videoUrl || null,
              lesson_order: lessons.indexOf(lesson) + 1,
              image_url: null,
            });
            
          if (lessonError) {
            console.error('Error inserting lesson:', lessonError);
            toast({
              title: "Error saving lesson",
              description: lessonError.message,
              variant: "destructive",
            });
          }
        }
      }
      
      toast({
        title: "Course created!",
        description: published ? "Your course has been published." : "Your draft has been saved.",
      });
      
      navigate('/courses');
    } catch (error: any) {
      console.error('Error creating course:', error.message);
      toast({
        title: "Failed to create course",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">
            <span className="gradient-text">Create Course</span>
          </h1>
          <Link to="/courses">
            <Button variant="outline">Back to Courses</Button>
          </Link>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <Card>
            <CardHeader>
              <CardTitle>Course Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Course Title</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter course title"
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="level">Level</Label>
                  <Input
                    id="level"
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                    placeholder="E.g., Beginner, Intermediate"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration">Duration</Label>
                  <Input
                    id="duration"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    placeholder="E.g., 8 weeks, 10 hours"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">Price</Label>
                  <Input
                    id="price"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="E.g., $199"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={status}
                  onValueChange={setStatus}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Coming Soon">Coming Soon</SelectItem>
                    <SelectItem value="Enrolling">Enrolling</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="textDirection">Text Direction</Label>
                <Select value={textDirection} onValueChange={(value: "ltr" | "rtl") => setTextDirection(value)}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select text direction" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ltr">Left to Right (LTR)</SelectItem>
                    <SelectItem value="rtl">Right to Left (RTL)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500">Choose RTL for Arabic, Hebrew, or other right-to-left languages</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Card className="min-h-[300px]">
                  <CardContent className="p-0">
                    <SimpleRichTextEditor 
                      value={description} 
                      onChange={setDescription}
                      direction={textDirection}
                      placeholder="Write your course description here..."
                    />
                  </CardContent>
                </Card>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="whatYouLearn">What You'll Learn</Label>
                <div className="space-y-3">
                  {whatYouLearn.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={item}
                        onChange={(e) => updateLearningItem(index, e.target.value)}
                        placeholder="e.g., Master React fundamentals"
                      />
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon"
                        onClick={() => removeLearningItem(index)}
                        disabled={whatYouLearn.length === 1}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addLearningItem}
                    className="mt-2"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Learning Item
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Course Image</Label>
                <Card className="border-dashed cursor-pointer" onClick={() => courseImageInputRef.current?.click()}>
                  <CardContent className="flex flex-col items-center justify-center p-6">
                    {imagePreview ? (
                      <div className="w-full aspect-video relative">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="w-full h-full object-cover rounded-md"
                        />
                        <Button 
                          variant="secondary" 
                          size="sm" 
                          className="absolute top-2 right-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setImageFile(null);
                            setImagePreview(null);
                          }}
                        >
                          Change
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Image className="h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">Click to upload a course image</p>
                        <p className="text-xs text-gray-400 mt-1">PNG, JPG or GIF up to 5MB</p>
                      </>
                    )}
                    <input
                      ref={courseImageInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleCourseImageChange}
                    />
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Course Lessons</h2>
              <Button type="button" onClick={addLesson} variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Lesson
              </Button>
            </div>
            
            {lessons.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center p-8 text-center">
                  <Video className="h-10 w-10 text-gray-400 mb-2" />
                  <p className="text-gray-500">No lessons added yet</p>
                  <Button type="button" onClick={addLesson} variant="outline" className="mt-4">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Lesson
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {lessons.map((lesson, index) => (
                  <Card key={lesson.id} className="relative">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={() => removeLesson(lesson.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    
                    <CardHeader>
                      <CardTitle className="text-lg">Lesson {index + 1}</CardTitle>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor={`lesson-title-${lesson.id}`}>Lesson Title</Label>
                        <Input
                          id={`lesson-title-${lesson.id}`}
                          value={lesson.title}
                          onChange={(e) => updateLesson(lesson.id, 'title', e.target.value)}
                          placeholder="Enter lesson title"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor={`lesson-desc-${lesson.id}`}>Description</Label>
                        <Textarea
                          id={`lesson-desc-${lesson.id}`}
                          value={lesson.description}
                          onChange={(e) => updateLesson(lesson.id, 'description', e.target.value)}
                          placeholder="Describe what this lesson covers..."
                          required
                          rows={3}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor={`lesson-video-url-${lesson.id}`}>
                          <div className="flex items-center gap-2">
                            <LinkIcon size={16} />
                            <span>Video URL</span>
                          </div>
                        </Label>
                        <Input
                          id={`lesson-video-url-${lesson.id}`}
                          value={lesson.videoUrl}
                          onChange={(e) => updateLesson(lesson.id, 'videoUrl', e.target.value)}
                          placeholder="e.g., https://www.youtube.com/watch?v=..."
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-2">
                <Switch
                  id="published"
                  checked={published}
                  onCheckedChange={setPublished}
                />
                <Label htmlFor="published">
                  Publish immediately
                </Label>
              </div>
            </CardContent>
            <CardFooter className="flex justify-end space-x-2 pt-4">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => navigate('/courses')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : published ? "Publish Course" : "Save Draft"}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </div>
    </Layout>
  );
};

export default CreateCourse;
